/**
 * Session keep-alive hook for SpeedGauge Copilot.
 * 
 * Automatically refreshes the session TTL periodically to prevent timeout
 * during periods of inactivity (e.g., user reading responses or thinking).
 * 
 * The keep-alive heartbeat is triggered:
 * - On a fixed interval (default: every 10 minutes)
 * - When the user returns to the window after being idle
 * - When receiving a chat message response
 */

import { useEffect, useRef, useCallback } from 'react';
import { chatService } from '../services/chatService';
import { isSessionExpired } from '../utils/errorHandler';
import { logger } from '../utils/logger';

const moduleLogger = logger.getModule('useSessionKeepAlive');

interface UseSessionKeepAliveOptions {
  /**
   * Interval between keep-alive calls in milliseconds (default: 600000 = 10 minutes)
   */
  intervalMs?: number;
  /**
   * Whether to enable keep-alive on window visibility changes (default: true)
   */
  enableVisibilityTracking?: boolean;
}

/**
 * Hook to keep a session alive by periodically refreshing its activity.
 * 
 * This prevents session timeout when the user is idle but still reading
 * responses or thinking about the next question.
 * 
 * Usage:
 * ```tsx
 * const { isActive, error } = useSessionKeepAlive(sessionId, {
 *   intervalMs: 300000  // Keep alive every 5 minutes
 * });
 * ```
 */
export function useSessionKeepAlive(
  sessionId: string | null | undefined,
  options: UseSessionKeepAliveOptions = {}
) {
  const {
    intervalMs = 10 * 60 * 1000, // Default: 10 minutes
    enableVisibilityTracking = true
  } = options;

  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const lastKeepAliveRef = useRef<number>(0);
  const isSessionExpiredRef = useRef<boolean>(false);

  /**
   * Perform the keep-alive operation
   */
  const performKeepAlive = useCallback(async () => {
    if (!sessionId) {
      moduleLogger.debug('No session ID, skipping');
      return;
    }

    // Prevent calling too frequently (within 30 seconds)
    const now = Date.now();
    if (now - lastKeepAliveRef.current < 30 * 1000) {
      moduleLogger.debug('Called too recently, skipping');
      return;
    }

    try {
      await chatService.keepSessionAlive(sessionId);
      lastKeepAliveRef.current = now;
      isSessionExpiredRef.current = false;
    } catch (error: any) {
      moduleLogger.error('Failed to refresh session', error);

      if (isSessionExpired(error)) {
        moduleLogger.warn('Session has expired, disabling keep-alive');
        isSessionExpiredRef.current = true;
        // Stop the interval if session is expired
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
      }
    }
  }, [sessionId]);

  /**
   * Handle window visibility changes - refresh when user returns to window
   */
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      moduleLogger.debug('Window became visible, refreshing session');
      performKeepAlive();
    } else {
      moduleLogger.debug('Window became hidden, pausing keep-alive');
    }
  }, [performKeepAlive]);

  /**
   * Setup the keep-alive interval
   */
  useEffect(() => {
    if (!sessionId) {
      return;
    }

    moduleLogger.debug('Starting keep-alive interval', { intervalMs, sessionId });

    // Don't perform immediate keep-alive - wait for the first interval
    // This prevents unnecessary API calls on page load/refresh when session is still valid
    intervalIdRef.current = setInterval(() => {
      performKeepAlive();
    }, intervalMs);

    // Setup visibility tracking if enabled
    if (enableVisibilityTracking && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    // Cleanup on unmount or session change
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }

      if (enableVisibilityTracking && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }

      moduleLogger.debug('Stopped keep-alive for session', { sessionId });
    };
  }, [sessionId, intervalMs, enableVisibilityTracking, performKeepAlive, handleVisibilityChange]);

  return {
    isActive: !!sessionId && !isSessionExpiredRef.current,
    isSessionExpired: isSessionExpiredRef.current,
    performKeepAlive
  };
}
